import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { toast } from 'sonner';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import apiClient from '../../api/client';
import { cn } from '../../lib/utils';

const ProjectModal = ({ open, onClose, project, onSuccess, initialClientId }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_id: '',
    team_members: [],
    status: 'acquired',
    start_date: '',
    end_date: '',
    total_budget: 0,
    progress_percentage: 0
  });
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamOpen, setTeamOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchClients = React.useCallback(async () => {
    try {
      const response = await apiClient.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, []);

  const fetchTeamMembers = React.useCallback(async () => {
    try {
      const response = await apiClient.get('/team');
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchClients();
      fetchTeamMembers();
    }
  }, [open, fetchClients, fetchTeamMembers]);

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title || '',
        description: project.description || '',
        client_id: project.client_id || '',
        team_members: project.team_members || [],
        status: project.status || 'acquired',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        total_budget: project.total_budget || 0,
        progress_percentage: project.progress_percentage || 0
      });
    } else {
      setFormData({
        title: '',
        description: '',
        client_id: initialClientId || '',
        team_members: [],
        status: 'acquired',
        start_date: '',
        end_date: '',
        total_budget: 0,
        progress_percentage: 0
      });
    }
  }, [project, open, initialClientId]);

  const toggleTeamMember = (memberId) => {
    setFormData((prev) => {
      const exists = prev.team_members.includes(memberId);
      const nextTeam = exists
        ? prev.team_members.filter((id) => id !== memberId)
        : [...prev.team_members, memberId];
      return { ...prev, team_members: nextTeam };
    });
  };

  const removeTeamMember = (memberId) => {
    setFormData((prev) => ({
      ...prev,
      team_members: prev.team_members.filter((id) => id !== memberId)
    }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error('Project title is required');
      return false;
    }
    if (!formData.description.trim()) {
      toast.error('Project description is required');
      return false;
    }
    if (!formData.client_id) {
      toast.error('Please select a client');
      return false;
    }
    if (formData.total_budget < 0) {
      toast.error('Budget cannot be negative');
      return false;
    }
    if (formData.progress_percentage < 0 || formData.progress_percentage > 100) {
      toast.error('Progress must be between 0 and 100');
      return false;
    }
    if (formData.start_date && formData.end_date && new Date(formData.start_date) > new Date(formData.end_date)) {
      toast.error('End date must be after start date');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        total_budget: parseFloat(formData.total_budget),
        progress_percentage: parseInt(formData.progress_percentage)
      };
      
      if (project) {
        await apiClient.put(`/projects/${project.project_id}`, submitData);
        toast.success('Project updated successfully');
      } else {
        await apiClient.post('/projects', submitData);
        toast.success('Project created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="project-modal">
        <DialogHeader>
          <DialogTitle>{project ? 'Edit Project' : 'Create New Project'}</DialogTitle>
          <DialogDescription>
            {project ? 'Update project details' : 'Add a new project for your client'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Project Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Website Redesign"
              className="bg-slate-50"
              data-testid="project-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Detailed project description"
              className="bg-slate-50"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="client">Client *</Label>
            <Select value={formData.client_id} onValueChange={(value) => setFormData({ ...formData, client_id: value })}>
              <SelectTrigger className="bg-slate-50" data-testid="project-client-select">
                <SelectValue placeholder="Select a client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.client_id} value={client.client_id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Team Members</Label>
            <Popover open={teamOpen} onOpenChange={setTeamOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={teamOpen}
                  className="w-full justify-between bg-slate-50"
                  data-testid="project-team-combobox"
                >
                  {formData.team_members.length
                    ? `${formData.team_members.length} selected`
                    : 'Select team members'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search team members..." />
                  <CommandList>
                    <CommandEmpty>No team members found.</CommandEmpty>
                    <CommandGroup>
                      {teamMembers.map((member) => {
                        const selected = formData.team_members.includes(member.member_id);
                        return (
                          <CommandItem
                            key={member.member_id}
                            value={`${member.name} ${member.email} ${member.role}`}
                            onSelect={() => toggleTeamMember(member.member_id)}
                          >
                            <Check className={cn('mr-2 h-4 w-4', selected ? 'opacity-100' : 'opacity-0')} />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">{member.name}</span>
                              <span className="text-xs text-slate-500">{member.email}</span>
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {formData.team_members.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.team_members.map((memberId) => {
                  const member = teamMembers.find((m) => m.member_id === memberId);
                  return (
                    <Badge key={memberId} variant="secondary" className="flex items-center gap-1">
                      <span className="text-xs">{member?.name || memberId}</span>
                      <button
                        type="button"
                        onClick={() => removeTeamMember(memberId)}
                        className="rounded-full p-0.5 hover:bg-slate-200"
                        aria-label="Remove team member"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger className="bg-slate-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="acquired">Acquired</SelectItem>
                  <SelectItem value="ongoing">Ongoing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="budget">Total Budget ({getCurrencySymbol(user?.currency)})</Label>
              <Input
                id="budget"
                type="number"
                min="0"
                step="0.01"
                value={formData.total_budget}
                onChange={(e) => setFormData({ ...formData, total_budget: e.target.value })}
                placeholder="10000"
                className="bg-slate-50"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="bg-slate-50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="bg-slate-50"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="progress">Progress (%)</Label>
            <Input
              id="progress"
              type="number"
              min="0"
              max="100"
              value={formData.progress_percentage}
              onChange={(e) => setFormData({ ...formData, progress_percentage: e.target.value })}
              className="bg-slate-50"
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={loading} data-testid="save-project-button">
              {loading ? 'Saving...' : project ? 'Update Project' : 'Create Project'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectModal;
